<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal mx-6 pt-6">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">资金流水</h1>
            <p class="list-page-description">查看不可修改的财富资金流水和多批次明细。</p>
          </div>
        </div>
      </div>
    </template>

    <template #filters>
      <PageFilterBar class="mx-6" body-class="p-5" :show-refresh="true" :refresh-loading="loading" @refresh="loadLedgers">
        <div class="w-44"><label class="filter-label">流水编号</label><input v-model="filters.ledgerNo" class="form-input form-control-sm w-full"></div>
        <div class="w-36"><label class="filter-label">客户ID</label><input v-model.number="filters.wealthCustomerId" type="number" class="form-input form-control-sm w-full"></div>
        <div class="w-36"><label class="filter-label">合同ID</label><input v-model.number="filters.contractId" type="number" class="form-input form-control-sm w-full"></div>
        <div class="w-36">
          <label class="filter-label">操作类型</label>
          <select v-model="filters.operationType" class="form-select-native form-control-sm w-full"><option value="">全部</option><option v-for="item in operationTypeOptions" :key="item.value" :value="item.value">{{ item.label }}</option></select>
        </div>
        <div class="w-36">
          <label class="filter-label">方向</label>
          <select v-model="filters.direction" class="form-select-native form-control-sm w-full"><option value="">全部</option><option v-for="item in directionOptions" :key="item.value" :value="item.value">{{ item.label }}</option></select>
        </div>
        <template #searchActions>
          <button type="button" class="btn-primary" @click="handleSearch">搜索</button>
          <button type="button" class="btn-secondary" @click="handleReset">重置</button>
        </template>
      </PageFilterBar>
    </template>

    <div class="list-page-table-panel mx-6">
      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head"><tr><th class="data-table-th text-left">流水</th><th class="data-table-th text-left">类型</th><th class="data-table-th text-left">方向</th><th class="data-table-th text-left">客户/合同/批次</th><th class="data-table-th text-right">金额</th><th class="data-table-th text-right">账户本金前后</th><th class="data-table-th text-left">时间</th><th class="data-table-th text-center">操作</th></tr></thead>
            <tbody>
              <tr v-if="loading"><td colspan="8" class="data-table-empty">加载中...</td></tr>
              <tr v-else-if="rows.length === 0"><td colspan="8" class="data-table-empty">暂无流水</td></tr>
              <tr v-for="row in rows" v-else :key="row.id" class="data-table-row">
                <td class="data-table-td"><div class="font-mono text-xs">{{ row.ledgerNo }}</div><div class="font-mono text-xs text-slate-500">{{ row.accountingSerialNo }}</div></td>
                <td class="data-table-td"><WealthStatusBadge type="operation" :value="row.operationType" /></td>
                <td class="data-table-td"><WealthStatusBadge type="direction" :value="row.direction" /></td>
                <td class="data-table-td"><div>客户 {{ row.wealthCustomerId || '-' }}</div><div class="text-xs text-slate-500">合同 {{ row.contractId || '-' }} / 批次 {{ row.batchId || '-' }}</div></td>
                <td class="data-table-td is-right font-medium">¥{{ formatMoney(row.amount) }}</td>
                <td class="data-table-td is-right">¥{{ formatMoney(row.beforeAccountPrincipal) }} → ¥{{ formatMoney(row.afterAccountPrincipal) }}</td>
                <td class="data-table-td text-xs">{{ formatDateTime(row.createTime) }}</td>
                <td class="data-table-td is-center"><button class="table-action-button is-primary" title="明细" @click="openDetail(row)"><svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg></button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <AppPagination v-if="pagination.total > 0" class="pagination-bridge" :pagination="pagination" :page-sizes="PAGE_SIZE_OPTIONS" mode="simple" @page-change="handlePageChange" @size-change="handleSizeChange" />
    </div>

    <WealthDetailDrawer v-model="detailVisible" title="流水详情" :subtitle="selectedLedger?.ledgerNo">
      <div v-if="selectedLedger" class="space-y-5">
        <section class="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 p-4 md:grid-cols-4">
          <div><div class="text-xs text-slate-500">操作类型</div><WealthStatusBadge type="operation" :value="selectedLedger.operationType" /></div>
          <div><div class="text-xs text-slate-500">方向</div><WealthStatusBadge type="direction" :value="selectedLedger.direction" /></div>
          <div><div class="text-xs text-slate-500">金额</div><div class="font-medium">¥{{ formatMoney(selectedLedger.amount) }}</div></div>
          <div><div class="text-xs text-slate-500">备注</div><div>{{ selectedLedger.remark || '-' }}</div></div>
        </section>
        <section class="rounded-lg border border-slate-200">
          <div class="border-b border-slate-200 px-4 py-3"><h4 class="text-sm font-semibold">批次明细</h4></div>
          <div class="data-table-shell border-0 shadow-none">
            <div class="data-table-scroll">
              <table class="data-table text-sm">
                <thead class="data-table-head"><tr><th class="data-table-th text-left">批次ID</th><th class="data-table-th text-right">金额</th><th class="data-table-th text-right">批次本金前后</th></tr></thead>
                <tbody>
                  <tr v-if="details.length === 0"><td colspan="3" class="data-table-empty">暂无明细</td></tr>
                  <tr v-for="item in details" v-else :key="item.id" class="data-table-row"><td class="data-table-td">{{ item.batchId }}</td><td class="data-table-td is-right">¥{{ formatMoney(item.amount) }}</td><td class="data-table-td is-right">¥{{ formatMoney(item.beforeBatchPrincipal) }} → ¥{{ formatMoney(item.afterBatchPrincipal) }}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </WealthDetailDrawer>
  </ListPageShell>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import { getWealthFundLedgerBatchDetails, getWealthFundLedgerPage } from '@/api/wealth'
import WealthDetailDrawer from '../components/WealthDetailDrawer.vue'
import WealthStatusBadge from '../components/WealthStatusBadge.vue'
import { cleanPayload, directionOptions, formatDateTime, formatMoney, operationTypeOptions, PAGE_SIZE_OPTIONS, pagePayload, syncPageResult } from '../utils'

const filters = reactive({ ledgerNo: '', operationType: '', direction: '', wealthCustomerId: null, contractId: null })
const pagination = reactive({ current: 1, page: 1, size: 10, total: 0, pages: 1 })
const rows = ref([])
const loading = ref(false)
const detailVisible = ref(false)
const selectedLedger = ref(null)
const details = ref([])

async function loadLedgers() {
  loading.value = true
  try {
    const result = await getWealthFundLedgerPage(pagePayload(pagination, cleanPayload(filters)))
    rows.value = result?.records || []
    syncPageResult(pagination, result)
  } finally {
    loading.value = false
  }
}
function handleSearch() { pagination.current = 1; pagination.page = 1; loadLedgers() }
function handleReset() { Object.assign(filters, { ledgerNo: '', operationType: '', direction: '', wealthCustomerId: null, contractId: null }); handleSearch() }
function handlePageChange(page) { pagination.current = page; pagination.page = page; loadLedgers() }
function handleSizeChange(size) { pagination.size = size; handleSearch() }
async function openDetail(row) { selectedLedger.value = row; detailVisible.value = true; details.value = await getWealthFundLedgerBatchDetails(row.id) }
onMounted(loadLedgers)
</script>
